import React, { createContext, useContext, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { CARD_TOKENS, type CardLanguage, type LocalizedText } from '@/components/PlanCard'

/**
 * One translatable field of a card or paywall editor. It shows and edits the language
 * the editor is in; in French an empty field shows the English text as placeholder,
 * since that is what users will see. Token chips insert `{{…}}` at the cursor.
 */

/** The field the server refused, e.g. `blocks[2].text.en`; fields under it are marked. */
export const ErrorFieldContext = createContext<string | null>(null)

interface LocalizedInputProps {
  value: LocalizedText | undefined
  onChange: (value: LocalizedText) => void
  language: CardLanguage
  /** Where the server would report this field, e.g. `blocks[2].text`. */
  path?: string
  placeholder?: string
  /** What an empty field falls back to, shown as placeholder (a template's text, a default). */
  inherited?: string
  multiline?: boolean
  tokens?: boolean
  /** An optional field may be empty in English too. */
  optional?: boolean
  className?: string
}

const TOKEN_LABELS: Record<string, string> = {
  planName: 'plan name',
  price: 'price',
  interval: 'interval',
  period: 'every month / for one month',
  minutes: 'minutes',
  modules: 'modules',
}

const LocalizedInput: React.FC<LocalizedInputProps> = ({ value, onChange, language, path, placeholder, inherited, multiline, tokens = true, optional, className }) => {
  const errorField = useContext(ErrorFieldContext)
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const [focused, setFocused] = useState(false)
  const current = value?.[language] ?? ''
  const english = value?.en ?? ''

  const set = (text: string) => onChange({ ...(value ?? { en: '' }), [language]: text } as LocalizedText)

  const insert = (token: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? current.length
    const end = el?.selectionEnd ?? current.length
    const tag = `{{${token}}}`
    set(current.slice(0, start) + tag + current.slice(end))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + tag.length, start + tag.length)
    })
  }

  const missingEnglish = !optional && !inherited && language === 'en' && !english.trim()
  const refused = !!errorField && !!path && (errorField === path || errorField.startsWith(`${path}.`) || errorField.startsWith(`${path}[`))
  const hint = language === 'en' ? inherited || placeholder : current ? undefined : english || inherited || placeholder

  const Field = multiline ? 'textarea' : 'input'
  return (
    <div className={className}>
      <Field
        ref={ref}
        value={current}
        rows={multiline ? 2 : undefined}
        placeholder={hint}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={cn(
          'w-full rounded border px-2 py-1.5 text-sm focus:border-transparent focus:ring-1 focus:ring-primary',
          multiline && 'resize-y',
          missingEnglish || refused ? 'border-red-300 bg-red-50/40' : language !== 'en' && !current && english ? 'border-amber-300 bg-amber-50/40' : 'border-gray-300',
        )}
      />
      {tokens && focused && (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-gray-500">Insert:</span>
          {CARD_TOKENS.map((token) => (
            <button
              key={token}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                insert(token)
              }}
              className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-100"
            >
              {TOKEN_LABELS[token]}
            </button>
          ))}
          <span className="text-[10px] text-gray-400">· **bold**</span>
        </div>
      )}
    </div>
  )
}

export default LocalizedInput
